import { useContext, useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import { useUserStore } from 'Store/User'
import { useRoleStore } from 'Store/Roles'
import { UserListViewContext } from '../List/Context'
import { UserFormModalContext } from './Context'
import * as Yup from 'yup'
import { shallow } from 'zustand/shallow'
import { toast } from 'react-toastify'

const INITIAL_VALUE = {
  username: '',
  fullName: '',
  roleId: '',
  password: '',
}

const VALIDATION_SCHEMA = Yup.object().shape({
  username: Yup.string()
    .matches(/^[A-Za-z0-9.]+$/, 'Username อนุญาตเฉพาะ A-Z, a-z และตัวเลข เท่านั้น')
    .required('กรุณากรอกชื่อผู้ใช้งาน'),
  fullName: Yup.string().required('กรุณากรอกชื่อ-นามสกุล'),
  roleId: Yup.number().required('กรุณาเลือกตำแหน่ง'),
  password: Yup.string().min(6, 'รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร'),
})

const ViewModel = () => {
  const { isOpenUserModal, toggleUserModal } = useContext(UserListViewContext)
  const { formType } = useContext(UserFormModalContext)
  const { getAll, selected, create, update } = useUserStore(
    state => ({
      getAll: state.getAll,
      selected: state.selected,
      create: state.create,
      update: state.edit,
    }),
    shallow
  )
  const { roles, getRoles, clearStateRoles } = useRoleStore(state => ({
    roles: state.data,
    getRoles: state.getAll,
    clearStateRoles: state.clearState,
  }))
  const [isLoadingRoles, setIsLoadingRoles] = useState(false)
  const roleOptions = useMemo(
    () =>
      roles.map((r: any) => ({
        label: r.title,
        value: r.id,
      })),
    [roles]
  )

  const submit = async ({
    username,
    fullName,
    roleId,
    password,
  }: {
    username: string
    fullName: string
    roleId: string
    password: string
  }) => {
    try {
      if (formType === 'add') {
        const { success, data } = await create({ username, fullName, roleId, password })
        if (!success) {
          console.log('error', data)
          toast.error(data)
          return false
        } else {
          toast.success('เพิ่มผู้ใช้งานสำเร็จ')
          await getAll()
          toggleUserModal(false)
          return true
        }
      } else if (formType === 'edit') {
        const { success, data } = await update(selected?.id, {
          username,
          fullName,
          roleId,
          password,
        })

        if (!success) {
          toast.error(data)
          return false
        } else {
          toast.success('แก้ไขผู้ใช้งานสำเร็จ')
          await getAll()
          toggleUserModal(false)
          return true
        }
      }
    } catch (err: any) {
      console.log(err)
      toast.error(err?.message)
      return false
    }
  }

  const fetchData = () => {
    if (isOpenUserModal) {
      setIsLoadingRoles(true)
      getRoles().then(() => {
        setIsLoadingRoles(false)
      })
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: INITIAL_VALUE,
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: async (values, { resetForm }) => {
      const status = await submit(values)
      if (status) resetForm()
    },
  })

  const handleClose = () => {
    clearStateRoles()
    toggleUserModal(false)
    formik.resetForm()
  }

  const modalsRoot = document.getElementById('root-modals') || document.body

  useEffect(() => {
    if (formType === 'edit' && selected) {
      formik.setValues({
        username: selected.username,
        fullName: selected.fullName,
        roleId: selected.roleId,
        password: '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formType, selected])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenUserModal])

  return {
    modalsRoot,
    formType,
    isLoadingRoles,
    isOpenUserModal,
    roleOptions,
    handleClose,
    formik,
  }
}

export default ViewModel
